import type { NodeCategoryModel } from 'src/modules/network-configuration/models/node-data';
import { IConfigurationContentData } from 'src/types/configuration';
import { ProjectJsonData } from 'src/types/project';

import { IDecodedCfgFile } from '../types';
import { buildSwitchesFromCfg } from './import-topology-build-switch';
import { appendCfgEdgesForSwitchContent } from './import-topology-cfg-edges';
import { normalizeName } from './import-topology-shared';
import { DeviceIpByNodeAndVlan } from './import-topology-types';
import type { ImportContent } from './import-topology-utils';
import {
  safeContent,
  collectVlansAndSubnet,
  applyDeviceVlansAndIps,
  layoutSwitchesAndDevices,
} from './import-topology-utils';

export const emptyProjectJsonData = (): ProjectJsonData => ({
  nodes: [],
  edges: [],
  vlans: [],
  globalIpSubnet: undefined,
  useCustomVlan: false,
  projectData: {},
  globalSettings: undefined,
  lastChanged: new Date().toISOString(),
});

export function importTopologyFromCfg(
  decodedFiles: IDecodedCfgFile<IConfigurationContentData>[],
): ProjectJsonData {
  const contents = decodedFiles.map(safeContent).flat();
  if (!contents.length) {
    return emptyProjectJsonData();
  }

  const { switchNodes, nodesByUniqueId } = buildSwitchesFromCfg(contents);

  const { vlans, useCustomVlan, globalIpSubnet } =
    collectVlansAndSubnet(contents);

  const nodesByNormalized = new Map<string, string>();

  switchNodes.forEach((n) => {
    const { name } = n.data.category as NodeCategoryModel;
    if (name) nodesByNormalized.set(normalizeName(name), n.id);
  });

  const deviceNodes: ProjectJsonData['nodes'] = [];

  const edges: ProjectJsonData['edges'] = [];
  const deviceVlans = new Map<string, Set<number>>();
  const deviceIpByNodeAndVlan = new Map<string, DeviceIpByNodeAndVlan>();

  const edgeKeys = new Set<string>();
  const switchPairConnected = new Set<string>();
  const deviceGatewayByVlan = new Map<number, string>();
  const deviceIdsUsingGatewayFallback = new Set<string>();

  contents.forEach((c) => {
    (c.data.dhcp?.subnets ?? []).forEach((s) => {
      if (!s?.ip) return;
      const firstRouterIp = (s.options ?? []).find(
        (o) =>
          o?.code === 3 &&
          Array.isArray(o.value) &&
          typeof o.value[0] === 'string',
      )?.value?.[0] as string | undefined;
      if (!firstRouterIp) return;
      const vlan = vlans.find((v) => v.ipSubnet === s.ip);
      if (vlan && !deviceGatewayByVlan.has(vlan.vlanId)) {
        deviceGatewayByVlan.set(vlan.vlanId, firstRouterIp);
      }
    });
  });

  const contentBySwitchId = new Map<string, ImportContent>();
  contents.forEach((c) => {
    const sid = nodesByUniqueId.get(c.uniqueId);
    if (sid) contentBySwitchId.set(sid, c);
  });

  contents.forEach((c) => {
    const nodeId = nodesByUniqueId.get(c.uniqueId);
    if (!nodeId) return;
    const swNode = switchNodes.find((n) => n.id === nodeId);
    if (!swNode) return;

    appendCfgEdgesForSwitchContent({
      c,
      swNode,
      switchNodes,
      nodesByNormalized,
      deviceNodes,
      deviceVlans,
      deviceIpByNodeAndVlan,
      edges,
      edgeKeys,
      switchPairConnected,
      contentBySwitchId,
      deviceGatewayByVlan,
      deviceIdsUsingGatewayFallback,
    });
  });

  applyDeviceVlansAndIps({
    deviceNodes,
    deviceVlans,
    vlans,
    deviceIpByNodeAndVlan,
  });
  layoutSwitchesAndDevices({ switchNodes, deviceNodes, edges });

  return {
    nodes: [...switchNodes, ...deviceNodes],
    edges,
    vlans,
    globalIpSubnet,
    useCustomVlan,
    projectData: {},
    globalSettings: undefined,
    lastChanged: new Date().toISOString(),
  };
}
